import axiosClient from "./axiosClient";
import type { BankAccountMeta, LendingRate } from "./shared-types";

// ============================================================================
// Document Type Definitions
// ============================================================================

/**
 * Loan document types matching backend enum.
 * Used for categorizing uploaded documents.
 */
export enum LoanDocumentType {
  DRIVER_LICENSE = "DRIVER_LICENSE",
  PASSPORT = "PASSPORT",
  PAYG_INCOME = "PAYG_INCOME",
  BANK_STATEMENT = "BANK_STATEMENT",
  PROPERTY_REPORT = "PROPERTY_REPORT",
  GENERATED_BANK_STATEMENT = "GENERATED_BANK_STATEMENT",
  GENERATED_PROPERTY_REPORT = "GENERATED_PROPERTY_REPORT",
  OTHER = "OTHER",
}

/**
 * Filter for document types when fetching documents.
 */
export enum DocumentTypeFilter {
  GENERATED = "generated",
  UPLOADED = "uploaded",
}

/**
 * Configuration for a document type including display label and upload limits.
 */
export interface DocumentTypeConfig {
  /** Document type key */
  key: LoanDocumentType;
  /** Display label for UI */
  label: string;
  /** Maximum number of files allowed for upload. undefined = unlimited */
  maxFiles?: number;
  /** Document types that map to this category (for grouping) */
  types: LoanDocumentType[];
}

/**
 * Document type configuration with upload limits.
 * Limits are sourced from loan-application/supporting-documents/ApplicantDocumentsSection.jsx
 */
export const DOCUMENT_TYPE_CONFIG: DocumentTypeConfig[] = [
  {
    key: LoanDocumentType.DRIVER_LICENSE,
    label: "Drivers License",
    maxFiles: 2,
    types: [LoanDocumentType.DRIVER_LICENSE],
  },
  {
    key: LoanDocumentType.PASSPORT,
    label: "Passport",
    maxFiles: 1,
    types: [LoanDocumentType.PASSPORT],
  },
  {
    key: LoanDocumentType.PAYG_INCOME,
    label: "Two Recent Payslips",
    maxFiles: 2,
    types: [LoanDocumentType.PAYG_INCOME],
  },
  {
    key: LoanDocumentType.BANK_STATEMENT,
    label: "Bank Statements",
    maxFiles: undefined, // Unlimited
    types: [LoanDocumentType.BANK_STATEMENT],
  },
  {
    key: LoanDocumentType.OTHER,
    label: "All other documents",
    maxFiles: undefined, // Unlimited
    types: [], // Catch-all for unmatched types
  },
];

// Types based on backend DTOs
export interface TaskDto {
  id?: string;
  name: string;
  aiAgentId?: string | null;
}

export interface CreateColumnDto {
  name: string;
  orderIndex: number;
  warningDueDays?: number | null;
  priorityDueDays?: number | null;
  typeOfItems?: string;
}

export interface UpdateColumnDto {
  name?: string;
  orderIndex: number;
  warningDueDays?: number | null;
  priorityDueDays?: number | null;
  typeOfItems?: string;
}

export interface UpsertTasksDto {
  tasks: TaskDto[];
  deleteTaskIds?: string[];
}

export interface TaskResponse {
  id: string;
  name: string;
  aiAgentId?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ColumnResponse {
  id: string;
  companyId: string;
  name: string;
  orderIndex: number;
  isFixed: boolean;
  warningDueDays?: number | null;
  priorityDueDays?: number | null;
  isLast: boolean;
  typeOfItems: string;
  tasks: TaskResponse[];
  createdAt: Date;
  updatedAt: Date;
}

export interface UpdateScenarioMetadataDto {
  columnId?: string | null;
  manualPriority?: ScenarioCrmManualPriority | null;
  onHoldTo?: string | null;
}

export type ScenarioCrmManualPriority = "high" | "medium" | "low";

export interface ListLoansQueryParams {
  page?: number;
  limit?: number;
  columnId?: string;
  keyword?: string;
  priority?: ScenarioCrmManualPriority;
  userId?: string;
}

export interface LoanScenarioSummary {
  id: string;
  opportunityId?: string;
  name?: string;
  mainUserId?: string;
  options?: Record<string, any>;
  scenarioObject?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
  mainUser?: LoanUserSummary | null;
  isDraft?: boolean;
}

export interface LoanUserSummary {
  id: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phoneNumber?: string;
}

export interface CompletedTaskSummary {
  id: string;
  crmTaskId: string | null;
  createdAt: string;
  snapshotMetadata?: Record<string, any> | null;
}

export interface ScenarioMetadataItem {
  id: string;
  scenarioId: string;
  columnId?: string | null;
  columnName?: string | null;
  columnChangedAt?: string | null;
  manualPriority?: ScenarioCrmManualPriority | null;
  daysSinceColumnChanged: number;
  isModifyCompletedLoan?: boolean | null;
  onHoldTo?: string | null;
  onHoldAt?: string | null;
  previousColumnId?: string | null;
  scenario: LoanScenarioSummary | null;
  mainApplicant?: LoanUserSummary | null;
  completedTasks: CompletedTaskSummary[];
  createdAt: string;
  updatedAt: string;
}

export interface ListLoansResponse {
  data: ScenarioMetadataItem[];
  total: number;
  page: number;
  limit: number;
}

export const getColumns = (): Promise<ColumnResponse[]> => {
  const url = "/loan-crm/columns";
  return axiosClient.get(url) as unknown as Promise<ColumnResponse[]>;
};

export const createColumn = (data: CreateColumnDto): Promise<ColumnResponse> => {
  const url = "/loan-crm/columns";
  return axiosClient.post(url, data) as unknown as Promise<ColumnResponse>;
};

export const updateColumn = (columnId: string, data: UpdateColumnDto): Promise<ColumnResponse> => {
  const url = `/loan-crm/columns/${columnId}`;
  return axiosClient.patch(url, data) as unknown as Promise<ColumnResponse>;
};

export const deleteColumn = (columnId: string): Promise<void> => {
  const url = `/loan-crm/columns/${columnId}`;
  return axiosClient.delete(url) as unknown as Promise<void>;
};

export const upsertTasks = (columnId: string, data: UpsertTasksDto): Promise<void> => {
  const url = `/loan-crm/columns/${columnId}/tasks`;
  return axiosClient.put(url, data) as unknown as Promise<void>;
};

export const updateScenarioMetadata = (
  loanId: string,
  data: UpdateScenarioMetadataDto
): Promise<void> => {
  const url = `/loan-crm/loans/${loanId}/metadata`;
  return axiosClient.patch(url, data) as unknown as Promise<void>;
};

export interface UpdateLoanScenarioRequest {
  isDraft?: boolean;
  isLoanSubmitted?: boolean;
}

export const updateLoanScenario = (
  loanId: string,
  data: UpdateLoanScenarioRequest
): Promise<LoanScenarioSummary> => {
  const url = `/loan-crm/loans/${loanId}`;
  return axiosClient.post(url, data) as unknown as Promise<LoanScenarioSummary>;
};

export const getLoanScenario = (loanId: string): Promise<any> => {
  const url = `/loan-crm/loans/${loanId}`;
  return axiosClient.get(url) as unknown as Promise<any>;
};

// Types for updating scenario object data
export interface UpdateLoanScenarioObjectRequest {
  mainUserId: string;
  assets?: any[];
  debts?: any[];
  incomes?: any[];
  expenses?: any[];
  contactExtraInfo?: any;
  coApplicantData?: any[];
  loanQuiz?: any;
  lvrSetting?: any;
  desiredLoanAmount?: number;
  dependants?: number;
  scenarioType?: string;
  features?: string[];
  loanStates?: any;
  mainUserProfile?: {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
  };
}

/**
 * Updates the scenario object data for a loan.
 * This allows updating specific sections like debts, assets, incomes, expenses, etc.
 * Only provided fields will be updated; other fields remain unchanged.
 */
export const updateLoanScenarioObject = (
  loanId: string,
  data: UpdateLoanScenarioObjectRequest
): Promise<LoanScenarioSummary> => {
  const url = `/loan-crm/loans/${loanId}/scenario-object`;
  return axiosClient.patch(url, data) as unknown as Promise<LoanScenarioSummary>;
};

export const listLoans = (params: ListLoansQueryParams = {}): Promise<ListLoansResponse> => {
  const url = "/loan-crm/loans";
  return axiosClient.get(url, {
    params,
  }) as unknown as Promise<ListLoansResponse>;
};

export interface MarkTaskCompleteDto {
  lastRequestSent?: Date;
}

export interface CompletedTaskResponse {
  id: string;
  taskId: string | null;
  snapshotMetadata: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export const markTaskAsComplete = (
  loanId: string,
  taskId: string,
  data?: MarkTaskCompleteDto
): Promise<CompletedTaskResponse> => {
  const url = `/loan-crm/loans/${loanId}/tasks/${taskId}`;
  return axiosClient.post(url, data) as unknown as Promise<CompletedTaskResponse>;
};

export const uncompleteTask = (loanId: string, taskId: string): Promise<void> => {
  const url = `/loan-crm/loans/${loanId}/tasks/${taskId}`;
  return axiosClient.delete(url) as unknown as Promise<void>;
};

export const getCompletedTasksByColumn = (
  loanId: string,
  columnId: string
): Promise<CompletedTaskResponse[]> => {
  const url = `/loan-crm/loans/${loanId}/columns/${columnId}/tasks`;
  return axiosClient.get(url) as unknown as Promise<CompletedTaskResponse[]>;
};

export interface CountAlertHitsDto {
  columnIds: string[];
}

export interface CompanyAlertHitsResponse {
  columnId: string;
  total: number;
}

export const countAlertHitsByPriority = (
  priorityName: "high" | "medium" | "low",
  data: CountAlertHitsDto
): Promise<CompanyAlertHitsResponse[]> => {
  const url = `/loan-crm/priorities/${priorityName}/counter`;
  return axiosClient.post(url, data) as unknown as Promise<CompanyAlertHitsResponse[]>;
};

export interface ColumnStatsResponse {
  columnId: string;
  numberOfItems: number;
  loanAmount: string;
}

export const syncColumnStats = (): Promise<void> => {
  const url = "/loan-crm/columns/stats/sync";
  return axiosClient.post(url) as unknown as Promise<void>;
};

export interface SendLeadEmailRequest {
  email: string;
  isSentEmail?: boolean;
}

export interface SendLeadEmailResponse {
  scenarioId: string;
  metadataId: string;
}

export const getColumnStats = (
  month: string,
  columnId?: string
): Promise<ColumnStatsResponse[]> => {
  const url = "/loan-crm/columns/stats";
  return axiosClient.get(url, {
    params: {
      at: month,
      ...(columnId ? { columnId } : {}),
    },
  }) as unknown as Promise<ColumnStatsResponse[]>;
};

export const sendLeadEmail = (data: SendLeadEmailRequest): Promise<SendLeadEmailResponse> => {
  const url = "/loan-crm/leads";
  return axiosClient.post(url, data) as unknown as Promise<SendLeadEmailResponse>;
};

export interface DeleteOpportunityRequest {
  mainUserId: string;
}

export const deleteOpportunity = (
  scenarioId: string,
  data: DeleteOpportunityRequest
): Promise<void> => {
  const url = `/loan-crm/opportunities/${scenarioId}`;
  return axiosClient.delete(url, {
    data,
  }) as unknown as Promise<void>;
};

export interface PutOnHoldRequest {
  onHoldTo: string;
}

export const putOpportunityOnHold = (loanId: string, data: PutOnHoldRequest): Promise<void> => {
  const url = `/loan-crm/loans/${loanId}/on-hold`;
  return axiosClient.post(url, data) as unknown as Promise<void>;
};

export const removeOpportunityFromHold = (loanId: string): Promise<void> => {
  const url = `/loan-crm/loans/${loanId}/place-back`;
  return axiosClient.post(url) as unknown as Promise<void>;
};

export interface BankStatementGeneratorRequest {
  scenarioId: string;
  userId: string;
  selectedAccountIds: string[];
  fromDate?: string;
  toDate?: string;
}

export interface BankStatementTransaction {
  id: string;
  status: string;
  description: string;
  amount: number;
  account: string;
  balance?: number;
  direction: string;
  type: string;
  subType: string;
  categoryId?: string;
  category?: string;
  categoryType?: string;
  detailCategoryId?: string;
  date: string;
  transactionDate: string;
  postDate: string;
  currency: string;
  isRentalIncome?: boolean;
  accountType: string;
  tags?: string[];
  accountHolder?: string;
  location?: string;
  fromEnrichTags?: boolean;
  connectionId?: string;
}

export interface BankStatementAccount {
  id: string;
  name?: string;
  currency?: string;
  balance?: number;
  availableFunds?: string;
  institution?: string;
  isAsset?: boolean;
  class?: {
    type?: string;
    product?: string;
  };
  totalCreditLine?: {
    currency?: string;
    amount?: number;
  };
  loanRepaymentAmount?: number;
  institutionName?: string;
  institutionShortName?: string;
  institutionLogo?: string | null;
  institutionLogoBase64?: string | null;
  accountHolder?: string;
  externalId?: string;
  ownerName?: string;
  accountNo?: string;
  bsb?: string;
  meta?: BankAccountMeta;
}

export interface BankStatementAccountTransactions {
  accountId: string;
  transactions: BankStatementTransaction[];
}

export interface BankStatementAccountData {
  account: BankStatementAccount;
  transactions: BankStatementTransaction[];
}

export interface BankStatementGeneratorResponse {
  data: BankStatementAccountTransactions[];
}

export const generateBankStatement = (
  data: BankStatementGeneratorRequest
): Promise<BankStatementGeneratorResponse> => {
  const url = "/loan-crm/bank-statement-generator";
  return axiosClient.post(url, data) as unknown as Promise<BankStatementGeneratorResponse>;
};

export interface LoanDocumentResponse {
  id: string;
  companyId: string;
  scenarioId: string;
  userId: string;
  brokerId?: string | null;
  fileName: string;
  fileSize?: number | null;
  s3Key?: string | null;
  s3Bucket?: string | null;
  status: string;
  documentType?: LoanDocumentType;
  dateRangeStart?: Date | null;
  dateRangeEnd?: Date | null;
  bankAccountId?: string | null;
  propertyId?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// Backward compatible alias
export type BankStatementDocumentResponse = LoanDocumentResponse;

export interface UploadBankStatementRequest {
  file: Blob | File;
  scenarioId: string;
  userId: string;
  statementType?: LoanDocumentType;
  bankAccountId?: string;
  propertyId?: string;
  dateRangeStart?: string;
  dateRangeEnd?: string;
}

export const uploadBankStatement = (
  data: UploadBankStatementRequest
): Promise<BankStatementDocumentResponse> => {
  const url = `/loan-crm/loans/${data.scenarioId}/documents`;
  const formData = new FormData();

  formData.append("file", data.file);
  formData.append("userId", data.userId);

  if (data.statementType) {
    formData.append("documentType", data.statementType);
  }
  if (data.bankAccountId) {
    formData.append("bankAccountId", data.bankAccountId);
  }
  if (data.propertyId) {
    formData.append("propertyId", data.propertyId);
  }
  if (data.dateRangeStart) {
    formData.append("dateRangeStart", data.dateRangeStart);
  }
  if (data.dateRangeEnd) {
    formData.append("dateRangeEnd", data.dateRangeEnd);
  }

  return axiosClient.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }) as unknown as Promise<BankStatementDocumentResponse>;
};

export interface PresignedDownloadUrlResponse {
  url: string;
  expiresAt: string;
}

export const getBankStatementDownloadUrl = (
  loanId: string,
  statementId: string
): Promise<PresignedDownloadUrlResponse> => {
  const url = `/loan-crm/loans/${loanId}/documents/${statementId}/download-urls`;
  return axiosClient.get(url) as unknown as Promise<PresignedDownloadUrlResponse>;
};

export interface ApplicantDocuments {
  userId: string;
  isMainApplicant: boolean;
  applicantName?: string;
  documents: LoanDocumentResponse[];
}

// Backward compatible alias
export type ApplicantBankStatements = ApplicantDocuments;

export interface GroupedLoanDocumentsResponse {
  applicants: ApplicantDocuments[];
}

// Backward compatible alias
export type GroupedBankStatementDocumentsResponse = GroupedLoanDocumentsResponse;

export const getBankStatements = (
  scenarioId: string,
  documentTypeFilter?: DocumentTypeFilter
): Promise<GroupedBankStatementDocumentsResponse> => {
  const params = new URLSearchParams();
  if (documentTypeFilter) {
    params.set("documentTypeFilter", documentTypeFilter);
  }
  const queryString = params.toString();
  const url = `/loan-crm/loans/${scenarioId}/documents${queryString ? `?${queryString}` : ""}`;
  return axiosClient.get(url) as unknown as Promise<GroupedBankStatementDocumentsResponse>;
};

export const deleteBankStatement = (loanId: string, statementId: string): Promise<void> => {
  const url = `/loan-crm/loans/${loanId}/documents/${statementId}`;
  return axiosClient.delete(url) as unknown as Promise<void>;
};

// ============================================================================
// Property Report Types and API Methods
// ============================================================================

export interface PropertyReportGeneratorRequest {
  scenarioId: string;
  userId: string;
  selectedPropertyIds: string[];
}

export interface PropertyData {
  propertyId: string;
  address: string;
  propertyType?: string;
  bedrooms?: number;
  bathrooms?: number;
  landArea?: number;
  carParks?: number;
  estimate?: number;
  monthlyRent?: number;
  purpose?: string;
  externalPropertyId?: string;
  avmData?: any;
}

export interface PropertyReportGeneratorResponse {
  data: LoanDocumentResponse[];
}

// Property reports reuse BankStatementDocumentResponse type
export type PropertyReportDocumentResponse = BankStatementDocumentResponse;
export type ApplicantPropertyReports = ApplicantBankStatements;
export type GroupedPropertyReportDocumentsResponse = GroupedBankStatementDocumentsResponse;

export const generatePropertyReport = (
  data: PropertyReportGeneratorRequest
): Promise<PropertyReportGeneratorResponse> => {
  const url = "/loan-crm/property-report-generator";
  return axiosClient.post(url, data) as unknown as Promise<PropertyReportGeneratorResponse>;
};

export const uploadPropertyReport = (data: {
  file: Blob | File;
  scenarioId: string;
  userId: string;
  propertyId?: string;
}): Promise<PropertyReportDocumentResponse> => {
  return uploadBankStatement({
    ...data,
    statementType: LoanDocumentType.PROPERTY_REPORT,
  });
};

export const getPropertyReportDownloadUrl = (
  loanId: string,
  reportId: string
): Promise<PresignedDownloadUrlResponse> => {
  return getBankStatementDownloadUrl(loanId, reportId);
};

export const getPropertyReports = (
  scenarioId: string
): Promise<GroupedPropertyReportDocumentsResponse> => {
  const url = `/loan-crm/loans/${scenarioId}/documents`;
  return axiosClient.get(url, {
    params: { type: "PROPERTY_REPORT" },
  }) as unknown as Promise<GroupedPropertyReportDocumentsResponse>;
};

export const deletePropertyReport = (loanId: string, reportId: string): Promise<void> => {
  return deleteBankStatement(loanId, reportId);
};

// ============================================================================
// Property List Types and API
// ============================================================================

export interface PropertyItemResponse {
  id: string;
  address?: string;
  propertyType?: string;
  estimateValue?: number;
  ownerEmail?: string;
}

export interface GetPropertiesResponse {
  properties: PropertyItemResponse[];
}

export const getLoanProperties = (scenarioId: string): Promise<GetPropertiesResponse> => {
  const url = `/loan-crm/loans/${scenarioId}/properties`;
  return axiosClient.get(url) as unknown as Promise<GetPropertiesResponse>;
};

// ============================================================================
// Property Search API
// ============================================================================

export interface PropertySearchResponse {
  addresses: [string, string][]; // [propertyId, address]
}

export const searchPropertyByAddress = (query: string): Promise<PropertySearchResponse> => {
  const url = "/properties/search";
  return axiosClient.get(url, {
    params: { addresses: query },
  }) as unknown as Promise<PropertySearchResponse>;
};

// ============================================================================
// Property Details API
// ============================================================================

export interface PropertyDetailsResponse {
  propertyId: string;
  address: string;
  estimate: number;
  estimateHigh?: number;
  estimateLow?: number;
  propertyType?: string;
  bedrooms?: number;
  bathrooms?: number;
  carparks?: number;
}

export const getPropertyDetails = (propertyId: string): Promise<PropertyDetailsResponse> => {
  const url = `/properties/details/${propertyId}`;
  return axiosClient.get(url) as unknown as Promise<PropertyDetailsResponse>;
};

// ============================================================================
// CSV Export
// ============================================================================

export interface ExportContactSummaryCsvParams {
  emails?: string[];
  excludedEmails?: string[];
  isExportAll?: boolean;
  alertId?: string | null;
  contactParams?: Record<string, any>;
}

export const exportContactSummaryCsv = async (
  params: ExportContactSummaryCsvParams
): Promise<Blob> => {
  const url = "/loan-crm/export-csv";
  const response = await axiosClient.post(url, params);
  return new Blob([response as unknown as string], {
    type: "text/csv;charset=utf-8;",
  });
};
